import { Alert, App, Button, Card, Col, Form, Input, Row, Typography, theme } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { PageHeader } from '@/shared/ui/PageHeader'
import { BrandingLogoCard } from '@/features/branding/BrandingLogoCard'
import {
  BRANDING_RULES,
  fetchBranding,
  saveCompanyDetails,
  type CompanyDetails,
  type TenantBranding,
} from '@/features/branding/brandingApi'
import { PHONE_E164, normalizePhone } from '@/features/subscriber/subscriberApi'

type FormValues = Record<keyof CompanyDetails, string | undefined>

const blankToNull = (value: string | undefined) => value?.trim() || null

function toDetails(values: FormValues): CompanyDetails {
  return {
    companyName: blankToNull(values.companyName),
    address: blankToNull(values.address),
    phone: normalizePhone(values.phone),
    email: blankToNull(values.email),
    website: blankToNull(values.website),
    cardInstructions: blankToNull(values.cardInstructions),
  }
}

/** Logotip almashganda forma qayta yasalmasin — kiritilayotgan matn yo'qolardi. */
function detailsKey(branding: TenantBranding): string {
  const { logoUrl: _logo, ...details } = branding
  return JSON.stringify(details)
}

export function BrandingPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const editable = can('settings.update')
  const branding = useQuery({ queryKey: queryKeys.tenantBranding, queryFn: fetchBranding })

  return (
    <>
      <PageHeader title={t('nav.branding')} description={t('pageIntro.branding')} />
      {branding.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(branding.error, t)} />
      ) : null}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title={t('settings.branding.detailsTitle')} loading={branding.isPending}>
            {branding.data ? (
              <CompanyDetailsForm key={detailsKey(branding.data)} branding={branding.data} editable={editable} />
            ) : null}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <BrandingLogoCard branding={branding.data} loading={branding.isPending} editable={editable} />
        </Col>
      </Row>
    </>
  )
}

function CompanyDetailsForm({ branding, editable }: { branding: TenantBranding; editable: boolean }) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { message } = App.useApp()
  const { token } = theme.useToken()
  const queryClient = useQueryClient()
  const [form] = Form.useForm<FormValues>()

  const save = useMutation({
    mutationFn: saveCompanyDetails,
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.tenantBranding, next)
      void message.success(t('settings.branding.saved'))
    },
  })

  return (
    <Form<FormValues>
      form={form}
      layout="vertical"
      disabled={!editable || save.isPending}
      initialValues={{
        companyName: branding.companyName ?? undefined,
        address: branding.address ?? undefined,
        phone: branding.phone ?? undefined,
        email: branding.email ?? undefined,
        website: branding.website ?? undefined,
        cardInstructions: branding.cardInstructions ?? undefined,
      }}
    >
      <Typography.Paragraph type="secondary">{t('settings.branding.description')}</Typography.Paragraph>
      {save.error ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(save.error, t)} /> : null}

      <Form.Item
        name="companyName"
        label={t('settings.branding.companyName')}
        extra={t('settings.branding.companyNameHint')}
        rules={[{ max: BRANDING_RULES.companyNameMax }]}
      >
        <Input dir="auto" autoComplete="organization" maxLength={BRANDING_RULES.companyNameMax} placeholder={user?.tenant.name} />
      </Form.Item>

      <Form.Item name="address" label={t('settings.branding.address')} rules={[{ max: BRANDING_RULES.addressMax }]}>
        <Input.TextArea dir="auto" rows={2} maxLength={BRANDING_RULES.addressMax} showCount />
      </Form.Item>

      <Row gutter={12}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="phone"
            label={t('settings.branding.phone')}
            rules={[
              {
                validator: (_, value: string | undefined) => {
                  const clean = normalizePhone(value)
                  if (clean === null || PHONE_E164.test(clean)) return Promise.resolve()
                  return Promise.reject(new Error(t('settings.branding.phoneFormat')))
                },
              },
            ]}
          >
            <Input dir="ltr" inputMode="tel" autoComplete="tel" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="email"
            label={t('settings.branding.email')}
            rules={[
              { max: BRANDING_RULES.emailMax },
              { pattern: BRANDING_RULES.email, message: t('settings.branding.emailFormat') },
            ]}
          >
            <Input dir="ltr" inputMode="email" autoComplete="email" placeholder="info@example.com" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="website"
        label={t('settings.branding.website')}
        rules={[
          { max: BRANDING_RULES.websiteMax },
          { pattern: BRANDING_RULES.website, message: t('settings.branding.websiteFormat') },
        ]}
      >
        <Input dir="ltr" inputMode="url" autoComplete="url" placeholder="www.example.com" />
      </Form.Item>

      <Form.Item
        name="cardInstructions"
        label={t('settings.branding.instructions')}
        extra={t('settings.branding.instructionsHint')}
        rules={[{ max: BRANDING_RULES.cardInstructionsMax }]}
      >
        <Input.TextArea
          dir="auto"
          rows={3}
          maxLength={BRANDING_RULES.cardInstructionsMax}
          showCount
          style={{ marginBottom: token.fontSize * token.lineHeight }}
        />
      </Form.Item>

      {editable ? (
        <Button
          type="primary"
          loading={save.isPending}
          onClick={() => submitOnValid(form, (values) => save.mutate(toDetails(values)))}
        >
          {t('common.save')}
        </Button>
      ) : (
        <Typography.Text type="secondary">{t('settings.branding.readOnly')}</Typography.Text>
      )}
    </Form>
  )
}
